import React, { useMemo } from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import { GroupChannel } from '@sendbird/chat/groupChannel';

import type { SimpleShareContentProps } from 'src/components/ad/ExternalShareModal';
import { ExternalShareModal } from 'src/components/ad/ExternalShareModal';
import SearchRecommendedRealEstateModal from 'src/components/chat/SearchRecommendedRealEstateModal';

import type { icons } from 'src/components/ui/Icon';
import Icon from 'src/components/ui/Icon';
import type { Props as IconButtonProps } from 'src/components/ui/IconButton';
import IconButton from 'src/components/ui/IconButton';
import Menu, { MenuItem } from 'src/components/ui/Menu';
import type { TinyPreviewCardProps } from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import Typography from 'src/components/ui/Typography';
import SearchAllUsersAndGroupsModal from 'src/components/ui/SearchAllUsersAndGroupsModal';

import { colours } from 'src/constants/colours';

import TooltipButton from 'src/components/ui/TooltipButton';
import { useAddContentEventMutation } from 'src/hooks/__generated__/queries';
import useAppRouter from 'src/hooks/useAppRouter';
import useLoginModal from 'src/hooks/useLoginModal';
import useModalOpenState, { useShareAsPrivateMessageModal, useShareViaModal } from 'src/hooks/useModalOpenState';
import type { Ad, Medium, NewsPost, Post, User } from 'src/types/__generated__/graphql';
import { AdStatuses } from 'src/types/__generated__/graphql';
import { routePathContactsWithChannelUrl, routePathContactsWithUserId } from 'src/constants/router';
import formatPrice from 'src/utils/formatPrice';
import { styledComponent } from 'src/utils/styled';
import useCreatePost from 'src/hooks/useCreatePost';
import useUser from 'src/hooks/useUser';

// this component handles its own TooltipButton to avoid overlay conflicts with the menu
// see WOOP-7659

const StyledIconButton = styledComponent(IconButton)<{
    $styledColor: boolean;
}>`
    ${({ $styledColor }) => {
        if ($styledColor) {
            return `
                color: ${colours.primaryOpacity.main};
                background-color: ${colours.primaryOpacity.O08};
                &:hover {
                    background-color: ${colours.primaryOpacity.O04} !important;
                }
                &:focus {
                    background-color: ${colours.white.O87};
                }
                &:not(:focus) {
                    background-color: ${colours.white.main};
                }
                &:not(.MuiIconButton-colorPrimary):not(.MuiIconButton-colorSecondary) {
                    border: ${colours.white.main};
                }
                `;
        } else {
            return `
            &:hover {
                background-color: ${colours.primaryOpacity.O04} !important;
            }
            &:focus {
                background-color: ${colours.white.O87};
            }
            &:not(:focus) {
                background-color: ${colours.white.main};
            }
        `;
        }
    }}
`;
export type AdForSharedIcon = Pick<Ad, 'id' | 'media' | 'propertyType' | 'status' | 'price' | 'rent' | 'title'>;

export type NewsPostForSharedIcon = { featuredCategory?: TinyPreviewCardProps['tag'] } & Pick<
    NewsPost,
    'id' | 'media' | 'title'
>;

type Props = Omit<IconButtonProps, 'iconProps'> & {
    newsPost?: NewsPostForSharedIcon;
    ad?: AdForSharedIcon;
    post?: Partial<Post>;
    styledColor?: boolean;
    isEstimate?: boolean;
    iconProps?: Partial<Omit<IconProps, 'name'>>;
};

interface Option {
    key: string;
    onClick: (e?: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
    icon: keyof typeof icons;
    label: string;
}

const ShareIcon: React.FC<Props> = ({ newsPost, ad, post, styledColor, isEstimate, iconProps, ...buttonProps }) => {
    const { t, i18n } = useTranslation(['ad', 'contacts']);
    const router = useAppRouter();
    const currentUser = useUser();

    const { createAuthenticatedHandler } = useLoginModal();
    const [addContentEventMutation] = useAddContentEventMutation();

    const { open: openPostModal } = useCreatePost();

    const { props: searchContactModalProps, open: openSearchContactModal } = useShareAsPrivateMessageModal();
    const { props: shareModalProps, open: openShareModal } = useShareViaModal();
    const { props: searchRecommmendedAgentsModal, open: openSearchRecommendedAgentsModal } = useModalOpenState();

    const onRecipientSelection = async (selectedRecipient: User | GroupChannel) => {
        const contactRoute =
            selectedRecipient instanceof GroupChannel
                ? `${routePathContactsWithChannelUrl}${selectedRecipient.url}`
                : `${routePathContactsWithUserId}${selectedRecipient.id}`;
        if (contactRoute) {
            if (newsPost) {
                await router.push(`${contactRoute}&sharedNewsPostId=${newsPost.id}`);
                await addContentEventMutation({
                    variables: {
                        newsPostId: newsPost.id,
                        eventType: 'shared_internally',
                    },
                    fetchPolicy: 'no-cache',
                });
            }
            if (ad) {
                await router.push(`${contactRoute}&sharedAdId=${ad.id}`);
                await addContentEventMutation({
                    variables: {
                        adId: ad.id,
                        eventType: 'shared_internally',
                    },
                    fetchPolicy: 'no-cache',
                });
            }
            if (post) {
                await router.push(`${contactRoute}&sharedPostId=${post.id}`);
                await addContentEventMutation({
                    variables: {
                        postId: post.id,
                        eventType: 'shared_internally',
                    },
                    fetchPolicy: 'no-cache',
                });
            }
        }
    };

    const optionsItems = useMemo(() => {
        const items = [
            {
                key: 'SearchAllUsersAndGroupsModal',
                onClick: () => createAuthenticatedHandler(openSearchContactModal),
                icon: 'ChatPrimaryInactive',
                label: t('common:shareInPrivateMessage'),
            },
            {
                key: 'ShareViaButton',
                onClick: openShareModal,
                icon: 'ExternalLink',
                label: t('common:shareVia'),
            },
            ad &&
                !isEstimate &&
                currentUser?.isProfessionnal && {
                    key: 'ShareOnNewsFeed',
                    onClick: () => openPostModal({ adId: ad.id }),
                    icon: 'PostIcon',
                    label: t('common:shareOnMyNewsFeed'),
                },
        ].filter<Option>((option): option is Option => Boolean(option));
        return items;
    }, [
        ad,
        createAuthenticatedHandler,
        currentUser?.isProfessionnal,
        isEstimate,
        openPostModal,
        openSearchContactModal,
        openShareModal,
        t,
    ]);

    const externalShareModalProps = useMemo(() => {
        let shareContentProps: SimpleShareContentProps | undefined;
        if (post?.id) {
            shareContentProps = {
                type: 'post',
                id: post.id,
            };
        } else if (newsPost) {
            shareContentProps = {
                type: 'news-post',
                id: newsPost.id,
            };
        } else if (ad) {
            shareContentProps = {
                type: isEstimate ? 'estimate' : 'ad',
                id: ad.id,
            };
        }

        return {
            title: post?.owner?.displayName ?? newsPost?.title ?? ad?.title ?? '',
            shareContentProps,
        };
    }, [ad, isEstimate, newsPost, post]);

    if (
        (!newsPost && !ad && !post) ||
        (ad?.status && ![AdStatuses.Draft, AdStatuses.Available, AdStatuses.Ended].includes(ad.status))
    ) {
        return null;
    }

    const price = formatPrice({
        price: Number(ad?.price ?? ad?.rent),
        lang: i18n.language,
    });

    const styledIconButton = () => (
        <StyledIconButton
            $styledColor={Boolean(styledColor)}
            iconProps={{ name: 'Share', size: '1.5rem', ...iconProps }}
            aria-haspopup="true"
            sx={{
                width: '3rem',
                padding: '0.75rem 0.25rem',
            }}
            {...buttonProps}
        />
    );

    return (
        <>
            <Menu
                id="simple-menu"
                items={optionsItems.map(({ key, onClick, icon, label }) => (
                    <MenuItem key={key} onClick={onClick}>
                        <Icon name={icon} size="1rem" />
                        <Box ml={1}>
                            <Typography variant="h6">{label}</Typography>
                        </Box>
                    </MenuItem>
                ))}
            >
                <TooltipButton
                    title={t('common:tooltips.share')}
                    ButtonComponent={styledIconButton}
                    containerStyle={{ display: 'inline-flex' }}
                    buttonProps={{
                        newsPost,
                        ad,
                        iconProps,
                    }}
                />
            </Menu>
            {externalShareModalProps.shareContentProps && (
                <ExternalShareModal
                    {...shareModalProps}
                    externalShareTitle={externalShareModalProps.title}
                    shareContentProps={externalShareModalProps.shareContentProps}
                />
            )}
            {searchContactModalProps.open && (
                <SearchAllUsersAndGroupsModal
                    title={t('common:shareInPrivateMessage')}
                    topArea={
                        <>
                            {newsPost && (
                                <TinyPreviewCard
                                    iconName="NewsIcon"
                                    type="newsPost"
                                    tag={newsPost.featuredCategory}
                                    media={newsPost.media as Medium[]}
                                    subtitle={newsPost.title as string}
                                />
                            )}
                            {ad && (
                                <TinyPreviewCard
                                    iconName="AdIcon"
                                    ad={ad as Ad}
                                    type="ad"
                                    media={ad.media as Medium[]}
                                    subtitle={price}
                                    title={t(`ad:propertyTypes.${ad.propertyType}`)}
                                />
                            )}
                        </>
                    }
                    submitLabel={t('ad:share')}
                    onSelection={onRecipientSelection}
                    renderGroupConversationButton
                    showConversations
                    {...searchContactModalProps}
                />
            )}
            <SearchRecommendedRealEstateModal
                title={t('common:shareAdvisor')}
                ad={ad as Ad}
                submitLabel={t('ad:share')}
                onSelection={async (selectedUserId) => {
                    if (ad) {
                        await router.push(`${routePathContactsWithUserId}${selectedUserId}&sharedAdId=${ad.id}`);
                        await addContentEventMutation({
                            variables: {
                                adId: ad.id,
                                eventType: 'shared_internally',
                            },
                            fetchPolicy: 'no-cache',
                        });
                    }
                }}
                {...searchRecommmendedAgentsModal}
            />
        </>
    );
};

export default ShareIcon;
